import { filter, remove } from 'lodash-es';
import React, { useEffect, useState } from 'react';

import { track } from '~/src/dash/analytics/analytics';
import { IDashboardChartData, IDashboardChartDataKey } from '~/src/dash/api/dashboards/home';
import { IDashboardSubChartMetric } from '~/src/dash/components/charts/common';
import { MobileMultiLineChart } from '~/src/dash/components/charts/MobileMultiLineChart';
import { Row, Rows, Text, useIsMounted } from '~/src/dash/publicaUI';
import { LoadingState } from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { IActiveFilters } from '~/src/dash/views/dashboards/DashboardHome/common/common';
import { useMobileStore } from '~/src/dash/views/mobile/contexts';
import { MobileDashboardMetricToggleRow } from '~/src/dash/views/mobile/MobileBaseView/Home/MobileDashboardSubChart/MobileDashboardMetricToggleRow';

const extractActiveMetrics = (
  cfg: Record<string, IDashboardSubChartMetric>,
  activeFilters: IActiveFilters,
) => {
  return filter(Object.values(cfg), (metric) => {
    return !metric.isDisabled(activeFilters);
  });
};

interface IMobileDashboardSubChartProps {
  title: string;
  data: Partial<IDashboardChartData>[];
  loadState: LoadingState;
  metricsCfg: Record<string, IDashboardSubChartMetric>;
  orderedMetricKeys: IDashboardChartDataKey[];
  activeFilters: IActiveFilters;
}

export const MobileDashboardSubChart = ({
  title,
  data = [],
  loadState,
  metricsCfg,
  orderedMetricKeys,
  activeFilters,
}: IMobileDashboardSubChartProps) => {
  const { user } = useMobileStore();
  const isSuperUser = hasRoleSuperUser(user.user);

  const isMounted = useIsMounted();
  const [activeMetrics, setActiveMetrics] = useState(
    extractActiveMetrics(metricsCfg, activeFilters),
  );

  // for now, only a selected demand must force a metric check
  useEffect(() => {
    if (isMounted() && !!activeFilters.demand) {
      setActiveMetrics(extractActiveMetrics(metricsCfg, activeFilters));
    }
  }, [activeFilters.demand]);

  const handleChangeActiveMetric = (metricKey: string, isActive: boolean) => {
    const clonedActiveMetrics = [...activeMetrics];
    if (isActive) {
      remove(clonedActiveMetrics, { key: metricKey });
    } else {
      clonedActiveMetrics.push(metricsCfg[metricKey]);
    }
    setActiveMetrics(clonedActiveMetrics);
    track(`Mobile Dash Live - Select ${title}`, { isSuperUser, metric: metricKey }, user.user);
  };

  return (
    <Rows>
      <Row smallVerticalPadding>
        <Text small bold>
          {title}
        </Text>
      </Row>
      <Row smallVerticalPadding>
        <MobileDashboardMetricToggleRow
          metricsCfg={metricsCfg}
          orderedMetricKeys={orderedMetricKeys}
          activeMetrics={activeMetrics}
          handleChangeActiveMetric={handleChangeActiveMetric}
          activeFilters={activeFilters}
        />
      </Row>
      <Row>
        <MobileMultiLineChart
          loadState={loadState}
          data={data}
          metrics={activeMetrics}
          metricsCfg={metricsCfg}
        />
      </Row>
    </Rows>
  );
};
